/**
 * The collections screen, decided: which works to show, under which heading,
 * with what said on each card.
 *
 * Pure. Works paired with the collection they came from go in; cards, picker
 * groups and headings come out. The copy and filter table are
 * `collection.constants.ts`; the two loads, the selected collection and the
 * create/share actions are `useCollectionController.ts`.
 *
 * The structural rule here is that ownership is read through a collection
 * and never off a work. `PlacedWork` is the only place the two meet, and
 * `collectionChoices` cannot see a work at all, so nothing on this screen can
 * offer an owner the grants do not. A selection that names a collection the
 * reader no longer holds falls back to everything, in the heading and the
 * grid at once — see `selectedCollections`.
 */

import { atLeast, canCurate, canShare, isOwner } from "@ondo/shared";
import type { CollectionSummary, MeResponse, WorkSummary } from "@ondo/shared";

import { describeApiError } from "../lib/apiError.ts";
import type { Choice, ChoiceGroup } from "../lib/choices.ts";
import { ONDO_ID_PATTERN } from "../lib/constants.ts";
import { describeLocation, toOwnerRef, workBadge } from "../lib/works.ts";
import type { EntityNames } from "../lib/works.ts";
import type { OwnerRef, WorkBadge } from "../lib/works.ts";
import {
  ACCESS_BADGES,
  ACCESS_LABELS,
  ALL_COLLECTIONS,
  COLLECTION_COPY,
  STATUS_FILTERS,
} from "./collection.constants.ts";
import type { StatusFilterKey } from "./collection.constants.ts";

/** One collection as the list renders it, with every decision already made. */
export interface CollectionRow {
  id: string;
  name: string;
  badge: string;
  access: string;
  /** "3 works" — pre-formatted so the view does no counting or pluralising. */
  holdings: string;
  canAddWorks: boolean;
  canShare: boolean;
}

/**
 * A work together with the collection it was fetched from.
 *
 * This pair is the ONLY place a work and a collection meet, and that is the
 * design constraint made structural. `WorkSummary` carries no collection —
 * deliberately, see its comment in packages/shared/src/api/collections.ts — so
 * the association is created by the request that asked for it rather than read
 * off the row.
 *
 * There is no `ownerId` here to drift out of step with `collection_grants`: to
 * know who owns a work you must go through `collectionId`, and there is no
 * other route.
 */
export interface PlacedWork {
  work: WorkSummary;
  collectionId: string;
}

/**
 * One entry in the collection picker: "All collections", or a collection.
 *
 * `key` is `ALL_COLLECTIONS` or the collection's id. The shape is the shared
 * one, because the control that renders it — `lib/CollectionHead.tsx` — is the
 * dashboard's too.
 */
export type CollectionChoice = Choice;

/**
 * A run of choices under one heading.
 *
 * `label` is null for a run that needs none — the "All collections" entry, and
 * the whole list when the caller holds every collection the same way. Headings
 * that sit over the entire list say nothing.
 */
export type CollectionGroup = ChoiceGroup;

export interface StatusFilter {
  key: StatusFilterKey;
  label: string;
}

export interface WorkCard {
  id: string;
  /** Where this work's record lives. Decided here, never assembled in the card. */
  href: string;
  title: string;
  /** "Max Ernst, 1927" — attribution and date read as one line in a catalogue. */
  attribution: string;
  medium: string | null;
  /** Where it sits. Ownership is read through this and only through this. */
  placement: { collectionId: string; collectionName: string; owner: OwnerRef };
  badge: WorkBadge;
  /**
   * "In Storage · Geneva Freeport", "On loan · Tate Modern", or the withheld
   * copy. Never an empty string standing in for "somewhere".
   */
  location: string;
  /** True when the location is withheld by policy rather than simply unset. */
  locationWithheld: boolean;
  /**
   * A signed URL for the work's photograph, or null when there is none to show
   * — no image on the record, or bytes this caller was refused. The same
   * picture the dashboard strip shows, through the same `WorkThumb`.
   */
  imageUrl: string | null;
}

export function toCollectionRow(collection: CollectionSummary): CollectionRow {
  return {
    id: collection.id,
    name: collection.name,
    badge: ACCESS_BADGES[collection.level],
    access: ACCESS_LABELS[collection.level],
    holdings: countWorks(collection.workCount),
    canAddWorks: canCurate(collection.level),
    canShare: canShare(collection.level),
  };
}

/**
 * The collection picker, grouped over COLLECTIONS and nothing else.
 *
 * The signature is the guarantee: this function cannot see a work, so an option
 * can never be derived from one. Which entity owns a work is which collection
 * holds it — a `collection_grant` — and there is no field on a work that could
 * disagree with that.
 *
 * Returns [] when there is only one collection. A dropdown with one option is
 * chrome pretending to be a control.
 *
 * It replaced an Owner dropdown that offered Yours / Shared with me and nothing
 * finer, which could not answer "show me this collection" at all. The two
 * groupings survive as HEADINGS here, so the same distinction is still on
 * screen without being a second control that can contradict this one.
 */
export function collectionChoices(collections: CollectionSummary[]): CollectionGroup[] {
  if (collections.length <= 1) return [];

  const everything: CollectionGroup = {
    label: null,
    choices: [{ key: ALL_COLLECTIONS, label: COLLECTION_COPY.allCollections }],
  };

  const mine = collections.filter((collection) => isOwner(collection.level));
  const shared = collections.filter((collection) => !isOwner(collection.level));

  // Headings only when there is a mix to head.
  if (mine.length === 0 || shared.length === 0) {
    return [everything, { label: null, choices: collections.map(toChoice) }];
  }

  return [
    everything,
    { label: COLLECTION_COPY.yours, choices: mine.map(toChoice) },
    { label: COLLECTION_COPY.sharedWithMe, choices: shared.map(toChoice) },
  ];
}

/**
 * The collections a selection admits, or null for all of them.
 *
 * Null rather than every id, and that is the whole point of the signature: a
 * selection can outlive the collection it names — a grant revoked between
 * loads, a collection deleted in another tab — and falling back to everything
 * shows the caller their works rather than an empty grid they cannot explain.
 */
export function selectedCollections(
  collections: CollectionSummary[],
  selected: string,
): Set<string> | null {
  if (selected === ALL_COLLECTIONS) return null;
  return collections.some((collection) => collection.id === selected)
    ? new Set([selected])
    : null;
}

/**
 * What the head is showing, in words: one collection's name, or all of them.
 *
 * Derived through `selectedCollections`, so the heading cannot disagree with
 * the grid under it — a selection that names a collection nobody holds any more
 * falls back to everything in both places at once. With a single collection
 * "all of them" IS that collection, and naming it beats a heading reading "All
 * collections" over a grid of one.
 */
export function selectedCollectionName(
  collections: CollectionSummary[],
  selected: string,
): string {
  const allowed = selectedCollections(collections, selected);
  const shown =
    allowed === null
      ? collections.length === 1
        ? collections[0]
        : undefined
      : collections.find((collection) => allowed.has(collection.id));

  return shown?.name ?? COLLECTION_COPY.allCollections;
}

function toChoice(collection: CollectionSummary): CollectionChoice {
  return { key: collection.id, label: collection.name };
}

/**
 * Only the filters the registry can answer.
 *
 * Returns [] when "All" is the only survivor, and the view then renders no chip
 * row at all — a single-option filter is not a filter.
 */
export function offeredFilters(): StatusFilter[] {
  const available = STATUS_FILTERS.filter((filter) => filter.available);
  if (available.length <= 1) return [];

  return available.map((filter) => ({ key: filter.key, label: filter.label }));
}

/**
 * The cards to show, after the owner and status filters.
 *
 * Note what it takes: placed works and collections. There is no path from here
 * to an owner that does not go through a collection.
 */
export function selectCards(input: {
  placed: PlacedWork[];
  collections: CollectionSummary[];
  /** `ALL_COLLECTIONS`, or the id of the one collection to show. */
  selected: string;
  status: StatusFilterKey;
  /** '/collector/me/work'. Threaded from the workspace, not built from a role. */
  workBase: string;
  /**
   * Signed photograph URLs by work id. Empty while they are still being signed,
   * and a card missing from it shows its placeholder — the grid is never held
   * back for a picture.
   */
  images: Map<string, string>;
  /**
   * The entities the reader acts for, keyed by ONDO id — OND-52.
   *
   * OPTIONAL, and omitted means the two-state holder label every caller had
   * before. `entityNamesOf` builds it from the account read, so a card can name
   * the company that holds the picture without anything being looked up.
   */
  entities?: EntityNames;
}): WorkCard[] {
  const byId = new Map(input.collections.map((collection) => [collection.id, collection]));
  const allowed = selectedCollections(input.collections, input.selected);

  return input.placed
    .filter((placed) => !allowed || allowed.has(placed.collectionId))
    .filter((placed) => matchesStatus(placed.work, input.status))
    .flatMap((placed) => {
      const collection = byId.get(placed.collectionId);
      if (!collection) return [];

      return [
        toWorkCard(
          placed.work,
          collection,
          input.workBase,
          input.images.get(placed.work.id) ?? null,
          input.entities,
        ),
      ];
    });
}

export function toWorkCard(
  work: WorkSummary,
  collection: CollectionSummary,
  workBase: string,
  imageUrl: string | null = null,
  entities: EntityNames = new Map(),
): WorkCard {
  return {
    id: work.id,
    href: `${workBase}/${work.id}`,
    title: work.title,
    attribution: work.year ? `${work.attributedName}, ${work.year}` : work.attributedName,
    medium: work.medium,
    placement: {
      collectionId: collection.id,
      collectionName: collection.name,
      // OND-52. The ENTITY that holds it, when the reader can name it: a grid
      // spanning a person and two of their companies said "Yours" of every
      // card, which is true of none of them in particular.
      owner: toOwnerRef(collection, entities),
    },
    badge: workBadge(work),
    location: describeLocation(work),
    locationWithheld: work.state === null,
    imageUrl,
  };
}

export function validateCollectionName(name: string): string | null {
  return name.trim() === "" ? COLLECTION_COPY.nameRequired : null;
}

/** One principal a new collection could belong to. `ondoId` null is the caller. */
export interface OwnerChoice {
  ondoId: string | null;
  label: string;
}

/**
 * Who a new collection could belong to — OND-184 and OND-175.
 *
 * Until 14 October 2026 the answer was always "you": `POST /v1/collections` sent
 * a name alone, so a gallery could not own a collection, and a work it bought
 * was placed in the collection of whoever pressed Accept. Title went to the firm
 * and `ondo.work_owner_principal_id()` answered the director — so a dealer
 * leaving took the firm's stock with them, because nothing in the record said
 * the gallery had ever owned it.
 *
 * THE ADMIN RUNG, which is not this function being careful: it is the rung
 * `ondo.create_collection()` has required since 20260803110000, with its own
 * reason written on it — creating a collection in an organisation's name is "an
 * act of the organisation, not of anyone who happens to work there". An editor
 * offered the firm here would be offered a refusal.
 *
 * NOT A PRIVILEGE CHECK, deliberately. OND-61's four privileges exist because
 * the owner's four named bundles disagree with the three rungs in exactly four
 * places, and this is not one of them; `ondo.org_privilege`'s own comment says a
 * fifth value belongs there "only when a door reads it".
 *
 * Returns the empty list when there is nothing to choose, so the view renders no
 * picker rather than one with a single option — `capacitiesFor`'s rule, and the
 * whole of "a collector with no firm sees no question".
 */
export function ownerChoices(me: MeResponse | null): OwnerChoice[] {
  if (!me) return [];

  const firms = me.memberships
    .filter((seat) => seat.status === "active" && atLeast(seat.level, "admin"))
    .map((seat) => ({
      ondoId: seat.organisation.ondoId,
      label: seat.organisation.displayName,
    }));

  if (firms.length === 0) return [];

  return [{ ondoId: null, label: COLLECTION_COPY.ownerSelf }, ...firms];
}

/**
 * Whether a typed name would be accepted — the same rule, as a yes or no.
 *
 * The create forms clear themselves only for a name the controller will take:
 * an empty one is refused, and blanking the field would hide the field the
 * refusal is about.
 */
export function isCollectionNamed(name: string): boolean {
  return validateCollectionName(name) === null;
}

export function validateOndoId(value: string): string | null {
  return ONDO_ID_PATTERN.test(value.trim().toUpperCase())
    ? null
    : COLLECTION_COPY.ondoIdRequired;
}

/** See `describeApiError`; kept under this name for its controllers and tests. */
export function describeError(error: unknown): string {
  return describeApiError(error, COLLECTION_COPY.unknownError);
}

function matchesStatus(work: WorkSummary, status: StatusFilterKey): boolean {
  if (status === "all") return true;
  // A withheld state matches nothing but "All". Guessing would mean inventing a
  // status for a work whose status the caller was deliberately not shown.
  if (work.state === null) return false;

  switch (status) {
    case "on-loan":
      return work.state.onLoan;
    case "displayed":
      return !work.state.onLoan && work.state.locationKind === "displayed";
    case "in-storage":
      return !work.state.onLoan && work.state.locationKind === "in_storage";
    default:
      // `sold` is unavailable, so it never reaches here through the UI.
      // Matching nothing is the honest answer if it ever does.
      return false;
  }
}

function countWorks(count: number): string {
  if (count === 0) return "No works";
  return count === 1 ? "1 work" : `${count} works`;
}
